import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  AlertTitle,
  Avatar,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
  Button,
  Snackbar
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { LoadingButton } from '@mui/lab';
import { Header } from 'components/Header';
import { CustomerInfo } from '../services/types';
import { getCustomerInfo, getServiceConfigs } from '../services/api';
import { formatApiErrorMessage, logApiError } from '../services/errors';
import { applyCs1ProductCareOverrides, sortServiceConfigs } from '../utils/serviceConfig';
import { useServiceConfigs } from '../context/ServiceConfigContext';

import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import PhoneIphoneOutlinedIcon from '@mui/icons-material/PhoneIphoneOutlined';
import LightningBoltOutlinedIcon from '@mui/icons-material/FlashOnOutlined';
import BuildCircleOutlinedIcon from '@mui/icons-material/BuildCircleOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { safeCopy } from 'helpers/safeCopy';
import { PRODUCT_TYPES } from 'services/const';

const NUMERIC_SIZE_TYPES = new Set(['Rings', 'Necklaces & Chains']);

const ALPHANUMERIC_SIZE_TYPES = new Set(['Bracelets']);

interface CustomerOwnedLandingPageProps {
  externalIdentity: string;
  cartId: string;
}

const CustomerOwnedLandingPage: React.FC<CustomerOwnedLandingPageProps> = ({ externalIdentity, cartId }) => {
  const navigate = useNavigate();
  const { customer, setCustomer, setServiceType, setConfigs, setSelectedServices, setPhotoUploadInfo, storeId } = useServiceConfigs();

  const [metalType, setMetalType] = useState('Non-Gold');
  const [productType, setProductType] = useState('');
  const [condition, setCondition] = useState('Worn');
  const [measuredSize, setMeasuredSize] = useState('');
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false); // to show linear/inline progress separately if needed
  const [error, setError] = useState<string | null>(null);
  const [snack, setSnack] = useState<string | null>(null);

  const isCs1Store = storeId?.toUpperCase?.() === 'CS1';

  const showMeasuredSize =
    NUMERIC_SIZE_TYPES.has(productType) ||
    ALPHANUMERIC_SIZE_TYPES.has(productType);

  const measuredSizeRequired = NUMERIC_SIZE_TYPES.has(productType);

  const isAlphanumericSize = ALPHANUMERIC_SIZE_TYPES.has(productType);

  const handleService = (type: 'QUICK' | 'STANDARD') => {
    if (type === 'QUICK' && isCs1Store) return;
    setServiceType(type);
    navigate('/photo-upload', { state: { metalType, productType, measuredSize, condition } });
  };

 const isValidMeasuredSize = (value: string) => {
  const trimmed = value.trim();

  return /^\d+(\.\d+)?$/.test(trimmed) && Number(trimmed) > 0;
};
 const isValidBraceletSize = (value: string) => /^[a-zA-Z0-9. ]+$/.test(value.trim());

  const canProceed = useMemo(() => {
    const baseValid = !!customer && !!metalType && !!productType && !!condition;

    if (!baseValid) return false;

    // Rings + Necklaces
    if (measuredSizeRequired) {
      return Boolean(measuredSize?.trim()) && isValidMeasuredSize(measuredSize);
    }

      // Bracelets (optional, but if entered must be valid)
    if (isAlphanumericSize) {
      return (
      !measuredSize.trim() ||
      isValidBraceletSize(measuredSize)
      );
    }

    return true;
  }, [
    customer,
    metalType,
    productType,
    condition,
    measuredSize,
    measuredSizeRequired,
    isAlphanumericSize
  ]);

  const measuredSizeError =
  measuredSizeRequired &&
  !!measuredSize &&
  !isValidMeasuredSize(measuredSize);

  const braceletSizeError =
  isAlphanumericSize &&
  !!measuredSize &&
  !isValidBraceletSize(measuredSize);

  useEffect(() => {
    if (!externalIdentity) {
      setError('Missing authentication context');
      setLoading(false);
      return;
    }
    setConfigs(null);
    setSelectedServices([]);
    setPhotoUploadInfo({ photo: null, comment: '', tag: '' });
    setServiceType(null);
    setMetalType('Non-Gold');
    setProductType('');
    setCondition('Worn');
    setMeasuredSize('');
    setError(null);

    let cancelled = false;

    const fetchData = async () => {
      try {
        setLoading(true);
        setFetching(true);

        const [customerData, serviceConfigs] = await Promise.all([
          getCustomerInfo(externalIdentity, cartId),
          getServiceConfigs(externalIdentity),
        ]);

        if (cancelled) {
          return;
        }

        if (!customerData?.id) {
          setError('Please add the customer to the cart before proceeding.');
          return;
        }

        setCustomer(customerData as CustomerInfo);
        const ordered = sortServiceConfigs(
          applyCs1ProductCareOverrides(serviceConfigs, storeId)
        );
        setConfigs(ordered);
      } catch (err: any) {
        logApiError('Loading customer-owned landing data', err);
        setError(formatApiErrorMessage(err, 'Failed to fetch customer.'));
      } finally {
        if (!cancelled) {
          setLoading(false);
          setFetching(false);
        }
      }
    };
    fetchData();

    return () => {
      cancelled = true;
    };
  }, [
    externalIdentity,
    cartId,
    setConfigs,
    setSelectedServices,
    setPhotoUploadInfo,
    setServiceType,
    setCustomer,
    storeId,
  ]);

  useEffect(() => {
    if (!productType) {
      setMeasuredSize('');
      return;
    }
    const requiresNumericSize = NUMERIC_SIZE_TYPES.has(productType);
    const allowsAlphanumericSize = ALPHANUMERIC_SIZE_TYPES.has(productType);

    if (requiresNumericSize || allowsAlphanumericSize) {
      setMeasuredSize((prev) =>
        prev && prev !== 'No Size' ? prev : ''
      );
    } else {
      setMeasuredSize('No Size');
    }
  }, [productType]);

  if (loading) {
    return (
      <Box p={4} display="flex" justifyContent="center">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={4} display="flex" flexDirection="column" alignItems="center" gap={2}>
        <Alert severity="warning" variant="outlined" sx={{ width: '100%', maxWidth: 440 }}>
          <AlertTitle>Error</AlertTitle>
          {error}
        </Alert>
        <Button variant="outlined" onClick={() => navigate(-1)}>Go Back</Button>
      </Box>
    );
  }

  const shortenId = (id: string, start = 8, end = 4) =>
    id.length > start + end ? `${id.slice(0, start)}…${id.slice(-end)}` : id;


  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      <Header
        title={<Typography variant="h6">Guest Owned</Typography>}
        subtitle={customer ? `${customer.firstName} ${customer.lastName}` : undefined}
        align="left"
        sticky
        elevateOnScroll
        rightSlot={
          <Tooltip title="Info">
            <IconButton>
              <InfoOutlinedIcon />
            </IconButton>
          </Tooltip>
        }
      />

      {/* Customer summary */}
      {customer && (
        <Card
          sx={(theme) => ({
            mx: 2,
            mt: 2,
            borderRadius: 3,
            overflow: 'hidden',
            border: `1px solid ${alpha(theme.palette.divider, 0.85)}`,
            boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
          })}
        >
          <Box
            sx={(theme) => ({
              display: 'flex',
              alignItems: 'center',
              gap: 1.25,
              px: 2,
              py: 1.25,
              background:
                theme.palette.mode === 'light'
                  ? alpha(theme.palette.primary.main, 0.06)
                  : alpha(theme.palette.primary.main, 0.18),
              borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.18)}`,
            })}
          >
            <Avatar
              variant="rounded"
              sx={(theme) => ({
                width: 36,
                height: 36,
                borderRadius: 1.2,
                bgcolor: alpha(theme.palette.primary.main, 0.15),
                color: theme.palette.primary.main,
              })}
            >
              <PersonOutlineIcon fontSize="small" />
            </Avatar>
            <Typography variant="subtitle1" fontWeight={700} sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {customer.firstName} {customer.lastName}
            </Typography>
            <Chip
              size="small"
              variant="outlined"
              label={shortenId(customer.id || '')}
              title={`${customer.id}\nClick to copy`}
              onClick={async () => {
                const ok = await safeCopy(customer.id || '');
                setSnack(ok ? 'Customer ID copied' : 'Copy failed');
              }}
              icon={<ContentCopyIcon sx={{ fontSize: 14 }} />}
              sx={{
                ml: 'auto',
                cursor: 'pointer',
                '& .MuiChip-label': { maxWidth: 140, overflow: 'hidden' },
              }}
            />
          </Box>

          <CardContent sx={{ py: 2 }}>
            <Stack spacing={1.25} divider={<Divider flexItem />}>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Stack direction="row" alignItems="center" gap={1}>
                  <EmailOutlinedIcon fontSize="small" />
                  <Typography variant="body2" color="text.secondary">Email</Typography>
                </Stack>
                <Typography variant="body2" sx={{ textAlign: 'right', wordBreak: 'break-all' }}>
                  {customer.email || '—'}
                </Typography>
              </Stack>

              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Stack direction="row" alignItems="center" gap={1}>
                  <PhoneIphoneOutlinedIcon fontSize="small" />
                  <Typography variant="body2" color="text.secondary">Phone</Typography>
                </Stack>
                <Typography variant="body2" sx={{ textAlign: 'right', wordBreak: 'break-all' }}>
                  {customer.phone || '—'}
                </Typography>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Service configuration form */}
      <Paper
        elevation={0}
        sx={(theme) => ({
          mx: 2,
          mt: 2,
          p: 2,
          borderRadius: 3,
          border: `1px solid ${alpha(theme.palette.divider, 0.85)}`,
          background: alpha(theme.palette.primary.light, 0.05),
          backdropFilter: 'saturate(120%) blur(4px)',
        })}
      >
        <Stack spacing={2}>
          <TextField
            fullWidth
            select
            label="Metal"
            value={metalType}
            onChange={(e) => setMetalType(e.target.value)}
            sx={(theme) => ({
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                backgroundColor: alpha(theme.palette.background.paper, 0.6),
                transition: 'box-shadow .2s ease',
                '&:hover': { boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.08)}` },
                '&.Mui-focused': { boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.16)}` },
              },
            })}
          >
            <MenuItem value="Non-Gold">Non‑Gold</MenuItem>
            <MenuItem value="Gold">Gold</MenuItem>
          </TextField>

          <TextField
            fullWidth
            select
            label="Product Type"
            value={productType}
            onChange={(e) => setProductType(e.target.value)}
            sx={(theme) => ({
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                backgroundColor: alpha(theme.palette.background.paper, 0.6),
                transition: 'box-shadow .2s ease',
                '&:hover': { boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.08)}` },
                '&.Mui-focused': { boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.16)}` },
              },
            })}
          >
            {PRODUCT_TYPES.map((item) => (
              <MenuItem key={item.value} value={item.value}>
                {item.name}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            fullWidth
            select
            label="Condition"
            value={condition}
            onChange={(e) => setCondition(e.target.value)}
            sx={(theme) => ({
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                backgroundColor: alpha(theme.palette.background.paper, 0.6),
                transition: 'box-shadow .2s ease',
                '&:hover': { boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.08)}` },
                '&.Mui-focused': { boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.16)}` },
              },
            })}
          >
            <MenuItem value="New">New – Recently Purchased</MenuItem>
            <MenuItem value="Worn">Worn – Guest Owned</MenuItem>
          </TextField>

          <TextField
            fullWidth
            label={isAlphanumericSize ?"Enter Size":"Enter Numeric Size"}
            value={measuredSize}
            onChange={(e) => setMeasuredSize(e.target.value)}
            placeholder={
              !showMeasuredSize
                ? undefined
                : isAlphanumericSize
                  ? 'Enter Size'
                  : 'Enter Numeric Size'
            }
            disabled={!showMeasuredSize}
            error={measuredSizeError || braceletSizeError}
            helperText={
              !showMeasuredSize
                ? 'Automatically set for this product type'
                : measuredSizeRequired
                  ? measuredSizeError
                    ? 'Enter a valid numeric size'
                    : ''
                  : braceletSizeError
                    ? 'Enter a valid size'
                    : ''
            }
            sx={(theme) => ({
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                backgroundColor: alpha(theme.palette.background.paper, 0.6),
                transition: 'box-shadow .2s ease',
                '&:hover': { boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.08)}` },
                '&.Mui-focused': { boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.16)}` },
              },
            })}
          />
        </Stack>
      </Paper>

      {/* Actions */}
      <Box mt={3} mx={2} mb={4}>
        <Stack spacing={1.25}>
          {/* Quick Service button hidden for now; retain logic for future enablement */}
          {false && (
            <LoadingButton
              variant="contained"
              fullWidth
              color="primary"
              onClick={() => { handleService('QUICK') }}
              disabled={!canProceed || fetching || isCs1Store}
              loading={fetching}
              loadingPosition="start"
              startIcon={<LightningBoltOutlinedIcon />}
              sx={(theme) => ({
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 700,
                '&.Mui-disabled': {
                  color: theme.palette.text.disabled,
                  backgroundColor: alpha(theme.palette.primary.main, 0.12),
                  opacity: 1,
                },
              })}
            >
              Quick Service
            </LoadingButton>
          )}

          <Button
            variant="contained"
            fullWidth
            color="primary"
            onClick={() => { handleService('STANDARD') }}
            disabled={!canProceed}
            startIcon={<BuildCircleOutlinedIcon />}
            sx={(theme) => ({
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 700,
              '&.Mui-disabled': {
                color: theme.palette.text.disabled,
                backgroundColor: alpha(theme.palette.primary.main, 0.12),
                opacity: 1,
              },
            })}
          >
            Standard Service
          </Button>
        </Stack>
      </Box>
      <Snackbar
        open={!!snack}
        autoHideDuration={2000}
        onClose={() => setSnack(null)}
        message={snack ?? ''}
      />
    </Box>
  );
};

export default CustomerOwnedLandingPage;
